import { create } from 'zustand';

import { SECTION_ORDER, getSection } from './sections';
import type {
  ActiveRun,
  PostSubmit,
  PostSubmitOutcome,
  RunKind,
  SectionId,
  SectionStatus,
  SubmitSectionId,
  TourMode,
} from './types';

const STORAGE_PREFIX = 'yoyo.hostTour.v1.';

interface PersistedState {
  mode: TourMode;
  hasOpenedGuide: boolean;
  sections: Record<SectionId, SectionStatus>;
  paused: boolean;
  activeRun?: ActiveRun;
  /** Set when the host really submitted a form during a run of that section. */
  postSubmit?: PostSubmit;
}

export interface HostTourState extends PersistedState {
  /** Whose progress is loaded; null until hydrated. */
  userId: string | null;

  hydrate: (userId: string) => void;
  setMode: (mode: TourMode) => void;
  startRun: (sectionId: SectionId, stepIndex: number, kind: RunKind) => void;
  setStepIndex: (stepIndex: number) => void;
  /** Records that the tour is about to navigate; cleared once the route arrives. */
  beginNavigation: (path: string) => void;
  clearNavigation: () => void;
  endRun: () => void;
  pause: () => void;
  resume: () => void;
  /** Marks a section done or skipped and clears the run; the caller decides what starts next. */
  finishSection: (sectionId: SectionId, status: 'done' | 'skipped') => void;
  markGuideOpened: () => void;
  skipAll: () => void;
  replayAll: () => void;
  /** A real form submit happened while that section was running: close it and queue the follow-up card. */
  markFormSubmitted: (sectionId: SubmitSectionId, outcome: PostSubmitOutcome) => void;
  clearPostSubmit: () => void;
  /** Jump the active run of `sectionId` forward to `stepId` if it has not reached it yet. */
  advanceRunTo: (sectionId: SectionId, stepId: string) => void;
}

export function defaultSections(): Record<SectionId, SectionStatus> {
  return Object.fromEntries(SECTION_ORDER.map((id) => [id, 'pending'])) as Record<
    SectionId,
    SectionStatus
  >;
}

function defaultPersisted(): PersistedState {
  return {
    mode: 'unknown',
    hasOpenedGuide: false,
    sections: defaultSections(),
    paused: false,
    activeRun: undefined,
    postSubmit: undefined,
  };
}

function storageKey(userId: string) {
  return `${STORAGE_PREFIX}${userId}`;
}

function storage(): Storage | undefined {
  try {
    return globalThis.localStorage ?? undefined;
  } catch {
    return undefined;
  }
}

function load(userId: string): PersistedState {
  const base = defaultPersisted();
  try {
    const raw = storage()?.getItem(storageKey(userId));
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return {
      ...base,
      ...parsed,
      postSubmit: undefined,
      // New sections added after a host saved progress start as pending.
      sections: { ...base.sections, ...(parsed.sections ?? {}) },
    };
  } catch {
    return base;
  }
}

function save(userId: string | null, state: PersistedState) {
  if (!userId) return;
  try {
    // `postSubmit` is deliberately not persisted: the follow-up card describes
    // where an item landed at submit time, which can change while the tab is closed.
    const { mode, hasOpenedGuide, sections, paused, activeRun } = state;
    const persistedRun = activeRun
      ? { kind: activeRun.kind, sectionId: activeRun.sectionId, stepIndex: activeRun.stepIndex }
      : undefined;
    storage()?.setItem(
      storageKey(userId),
      JSON.stringify({ mode, hasOpenedGuide, sections, paused, activeRun: persistedRun })
    );
  } catch {
    // Storage unavailable (private mode, quota): the tour still works for this page load.
  }
}

export const useHostTourStore = create<HostTourState>()((set, get) => {
  const commit = (patch: Partial<PersistedState>) => {
    const next = { ...get(), ...patch };
    set(patch);
    save(next.userId, next);
  };

  return {
    ...defaultPersisted(),
    userId: null,

    hydrate: (userId) => {
      if (get().userId === userId) return;
      set({ userId, ...load(userId) });
    },

    setMode: (mode) => commit({ mode }),

    startRun: (sectionId, stepIndex, kind) =>
      commit({ activeRun: { sectionId, stepIndex, kind }, paused: false }),

    setStepIndex: (stepIndex) => {
      const run = get().activeRun;
      if (!run || run.stepIndex === stepIndex) return;
      commit({ activeRun: { ...run, stepIndex } });
    },

    beginNavigation: (path) => {
      const run = get().activeRun;
      if (!run) return;
      commit({ activeRun: { ...run, navigatingTo: path } });
    },

    clearNavigation: () => {
      const run = get().activeRun;
      if (!run?.navigatingTo) return;
      const { navigatingTo: _dropped, ...rest } = run;
      commit({ activeRun: rest });
    },

    endRun: () => commit({ activeRun: undefined, paused: false }),

    pause: () => commit({ paused: true }),

    resume: () => commit({ paused: false }),

    finishSection: (sectionId, status) => {
      const sections = { ...get().sections, [sectionId]: status };
      const allSettled = SECTION_ORDER.every((id) => sections[id] !== 'pending');
      commit({
        sections,
        activeRun: undefined,
        paused: false,
        mode: allSettled ? 'done' : get().mode,
      });
    },

    markGuideOpened: () => {
      if (get().hasOpenedGuide) return;
      commit({ hasOpenedGuide: true });
    },

    skipAll: () => {
      const sections = { ...get().sections };
      for (const id of SECTION_ORDER) if (sections[id] === 'pending') sections[id] = 'skipped';
      commit({
        sections,
        mode: 'done',
        activeRun: undefined,
        paused: false,
        postSubmit: undefined,
      });
    },

    replayAll: () =>
      commit({
        sections: defaultSections(),
        mode: 'guided',
        activeRun: undefined,
        paused: false,
        postSubmit: undefined,
      }),

    markFormSubmitted: (sectionId, outcome) => {
      const run = get().activeRun;
      if (!run || run.sectionId !== sectionId) return;
      commit({
        sections: { ...get().sections, [sectionId]: 'done' },
        activeRun: undefined,
        paused: false,
        postSubmit: { kind: run.kind, sectionId, outcome },
      });
    },

    advanceRunTo: (sectionId, stepId) => {
      const run = get().activeRun;
      if (!run || run.sectionId !== sectionId) return;
      const target = getSection(sectionId).steps.findIndex((s) => s.id === stepId);
      if (target === -1 || run.stepIndex >= target) return;
      commit({ activeRun: { ...run, stepIndex: target } });
    },

    clearPostSubmit: () => commit({ postSubmit: undefined }),
  };
});
